import { WidgetKey } from 'shared/constants/widgets'
import { AiArtifactConfig, PersistedWidgetSpec } from 'shared/types/widgets'

/**
 * Identity used by user-facing singleton rules. Standard widgets are unique
 * by type regardless of size; AI artifacts are unique by publicId so distinct
 * artifacts can coexist while the same artifact cannot be pinned twice.
 */
export const widgetSingletonKey = (spec: {
  widgetKey: WidgetKey
  config?: unknown
}): string => {
  if (spec.widgetKey !== WidgetKey.AI_ARTIFACT_TILE) {
    return `widget:${spec.widgetKey}`
  }

  const publicId =
    spec.config &&
    typeof spec.config === 'object' &&
    'publicId' in spec.config &&
    typeof (spec.config as AiArtifactConfig).publicId === 'string'
      ? (spec.config as AiArtifactConfig).publicId
      : ''
  return `ai:${publicId}`
}

/**
 * Derives the logical identity of a widget for dedup purposes.
 *
 * - AI_ARTIFACT_TILE: dedup by config.publicId — a given artifact can be
 *   pinned once, regardless of size.
 * - Everything else: dedup by widgetKey regardless of size. Size is a
 *   presentation choice for the one widget instance, not part of identity.
 *
 * Returns null when the spec is missing a widgetKey (incomplete/stale data);
 * callers should treat those items as not-dedupable and pass them through.
 */
export const widgetLogicalKey = (spec: {
  widgetKey: WidgetKey | undefined
  config?: unknown
}): string | null => {
  if (!spec.widgetKey) return null
  if (spec.widgetKey === WidgetKey.AI_ARTIFACT_TILE) {
    const publicId =
      spec.config &&
      typeof spec.config === 'object' &&
      'publicId' in spec.config &&
      typeof (spec.config as AiArtifactConfig).publicId === 'string'
        ? (spec.config as AiArtifactConfig).publicId
        : ''
    return `ai:${publicId}`
  }
  return widgetSingletonKey({ widgetKey: spec.widgetKey, config: spec.config })
}

/**
 * Dedupes a list of PersistedWidgetSpec by logical key. First occurrence wins.
 * See {@link widgetLogicalKey} for the key rules.
 */
export const dedupePersistedWidgetSpecs = (
  specs: PersistedWidgetSpec[]
): PersistedWidgetSpec[] => {
  const seen = new Set<string>()
  return specs.filter(spec => {
    const key = widgetLogicalKey({
      widgetKey: spec.widgetKey,
      config: spec.config
    })
    if (!key) return true
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}
